<template>
  <div class="vertical-slider-box vertical-slider-1" v-if="!isProjectDetailsMode" :key="`projects-${forceRenderKey}`">
    <div class="vertical-slider-row" @click="selectProject('all')" :class="{ active: selectedProject === 'all' }">
      <i>
        <img
          src="@/assets/lems-v1/images/projects/all-project-icon.svg"
          alt=""
        />
      </i>
      <span>All Projects</span>
    </div>

    <div 
      v-for="(project, index) in projects" 
      :key="`${project.key}-${index}`"
      class="vertical-slider-row" 
      @click="selectProject(project.key)"
      :class="{ active: selectedProject === project.key }"
    >
      <i>
        <img :src="project.avatarUrls['48x48']" alt="" />
      </i>
      <span>{{ project.name }}</span>
    </div>
  </div>
  
  <div class="project-status-chart">
    <div class="status-issue-type">
      <div class="status-issue-select">
        <select class="v1-selectbox" id="statusIssueTypeSelectBox" v-model="selectedIssueType" @change="filterByIssueType">
          <option value="all">All Issue Types</option>
          <option v-for="type in availableIssueTypes" :key="type" :value="type">
            {{ type }}
          </option>
        </select>
      </div>
    </div>
    <div class="chart_box">
      <div v-if="validChartData.length === 0" class="no-data">
        <p>No data available for the selected project</p>
      </div>
      <div v-else class="chart-container">
        
        <div class="donut-chart">
          <svg class="donut-chart-svg" viewBox="0 0 240 240">
            <g class="donut-segments">
              <path
                v-for="(item, index) in validChartData"
                :key="item.name"
                :d="getDonutPath(index, validChartData)"
                fill="none"
                :stroke="getColorForIssueType(item.name)"
                stroke-width="25"
                class="donut-segment"
                :title="`${item.name}: ${item.count} (${item.percentage}%)`"
                @mouseenter="showTooltip($event, item, index)"
                @mouseleave="hideTooltip"
              />
            </g>
            <text x="120" y="110" text-anchor="middle" class="donut-center-text">
              {{ getTotalIssues() }}
            </text>
            <text x="120" y="130" text-anchor="middle" class="donut-center-subtext">
              Total Issues
            </text>
          </svg>
        </div>
        <div v-if="tooltipVisible" class="chart-tooltip" :style="tooltipStyle">
          <div class="tooltip-content" :style="{ backgroundColor: getColorForIssueType(tooltipData?.name || '') }">
            <div class="tooltip-name">{{ tooltipData?.name }}</div>
            <div class="tooltip-count">{{ tooltipData?.count }} tickets</div>
            <div class="tooltip-percentage">{{ tooltipData?.percentage }}%</div>
          </div>
          <div class="tooltip-connector" :style="{ backgroundColor: getColorForIssueType(tooltipData?.name || '') }"></div>
        </div>
      </div>
      </div>
    </div>

  <div class="vertical-slider-box vertical-slider-2" :key="`issues-${forceRenderKey}`">
    <!-- <div v-if="loading" class="loading-container">
      <div class="loading-spinner">Loading statistics...</div>
    </div> -->
    <!-- <div v-else> -->
    <div 
      v-for="(item, index) in filteredIssueTypeStats" 
      :key="`${item.name}-${index}`"
      class="status-chart-details"
    >
      <div class="custom-row">
          <i class="status-colors" :style="{ backgroundColor: getColorForIssueType(item.name) }"></i>
          <div class="status-type">{{ item.name }}</div>
          <div class="status-numbr">{{ item.count }}</div>
          <div class="status-done">{{ item.percentage }}%</div>
    </div>
      </div>
    <!-- </div> -->
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import constants from "@/utils/en-US-Employee.json";
import axios from "axios";
import { API_BASE_URL } from "@/config/api.config";
import 'slick-carousel';
import $ from 'jquery';

interface Project {
  id: string;
  key: string;
  name: string;
  avatarUrls: {
    [key: string]: string;
  };
  lead: {
    displayName: string;
    avatarUrls: {
      [key: string]: string;
    };
  };
  total_issues: number;
  issue_type_stats: IssueTypeStat[];
  status_counts: {
    [key: string]: number;
  };
}

interface IssueTypeStat {
  name: string;
  count: number;
  percentage: number;
}

export default defineComponent({
  name: "ProjectStatus",
  data() {
    return {
      constants,
      projects: [] as Project[],
      selectedProject: 'all',
      selectedIssueType: 'all',
      loading: false,
      chartData: [] as IssueTypeStat[],
      availableIssueTypes: [] as string[],
      tooltipVisible: false,
      tooltipData: null as IssueTypeStat | null,
      tooltipStyle: {
        left: '0px',
        top: '0px'
      },
      hoveredSegmentIndex: -1,
      isProjectDetailsMode: false,
      forceRenderKey: 0
    };
  },
  computed: {
    filteredIssueTypeStats(): IssueTypeStat[] {
      if (this.selectedIssueType === 'all') {
        return this.chartData;
      }
      return this.chartData.filter(item => item.name === this.selectedIssueType);
    },
    
    validChartData(): IssueTypeStat[] {
      // Ensure chart data is valid and has proper percentages
      if (!this.chartData || this.chartData.length === 0) {
        return [];
      }
      
      // Filter out items with zero count
      const validData = this.chartData.filter(item => item.count > 0);
      
      if (validData.length === 0) {
        return [];
      }
      
      if (validData.length === 1) {
        // Single item gets 100%
        return [{ ...validData[0], percentage: 100 }];
      }
      
      // For multiple items, ensure minimum percentages
      const totalTickets = validData.reduce((sum, item) => sum + item.count, 0);
      
      if (totalTickets <= 5) {
        // For very small numbers, give equal percentages
        const equalPercentage = 100 / validData.length;
        return validData.map(item => ({
          ...item,
          percentage: Math.round(equalPercentage * 10) / 10
        }));
      }
      
      // For larger numbers, use actual percentages but ensure minimum visibility
      return validData.map(item => {
        let percentage = item.percentage;
        if (percentage < 3 && item.count > 0) {
          percentage = 3; // Minimum 3% for visibility
        }
        return { ...item, percentage };
      });
    }
  },
  mounted() {
    // Check if we're in project details mode
    this.isProjectDetailsMode = this.$route.name === 'project-detail';
    
    // If in project details mode, set the project key from route
    if (this.isProjectDetailsMode) {
      this.selectedProject = this.$route.params.projectKey as string;
    }
    
    this.getProjectStats();
  },
  beforeUnmount() {
    // Clean up sliders when component is destroyed
    this.cleanupSliders();
  },
  methods: {
    async getProjectStats() {
      const username = localStorage.getItem("jira_user_username");
      if (!username) return;

      this.loading = true;
      try {
        const response = await axios.get(`${API_BASE_URL}/jira/project-stats/${username}/`);
        this.projects = response.data.projects;
        this.updateChartData();
        this.updateAvailableIssueTypes();
        // Temporarily disable Slick to prevent DOM conflicts
        // this.$nextTick(() => {
        //   this.initializeAllProjectsSlider();
        //   this.initializeIssueTypeSlider();
        // });
      } catch (error) {
        console.error("Error fetching project statistics:", error);
      } finally {
        this.loading = false;
      }
    },
    selectProject(projectKey: string) {
      this.selectedProject = projectKey;
      this.cleanupSliders();
      this.updateChartData();
      this.forceRenderKey++;
      // Temporarily disable Slick to prevent DOM conflicts
      // this.$nextTick(() => {
      //   this.initializeAllProjectsSlider();
      //   this.initializeIssueTypeSlider();
      // });
    },
    filterByIssueType() {
      // This method is called when issue type filter changes
      // The computed property handles the filtering
      this.cleanupSliders();
      this.forceRenderKey++;
      // Temporarily disable Slick to prevent DOM conflicts
      // this.$nextTick(() => {
      //   this.initializeIssueTypeSlider();
      // });
    },
    updateChartData() {
      if (this.selectedProject === 'all') {
        // Aggregate data from all projects
        const aggregatedStats: { [key: string]: { count: number, total: number } } = {};
        let totalIssues = 0;

        this.projects.forEach(project => {
          totalIssues += project.total_issues;
          project.issue_type_stats.forEach(stat => {
            if (!aggregatedStats[stat.name]) {
              aggregatedStats[stat.name] = { count: 0, total: 0 };
            }
            aggregatedStats[stat.name].count += stat.count;
          });
        });

        this.chartData = Object.keys(aggregatedStats).map(name => ({
          name,
          count: aggregatedStats[name].count,
          percentage: totalIssues > 0 ? Math.round((aggregatedStats[name].count / totalIssues) * 100 * 10) / 10 : 0
        })).sort((a, b) => b.count - a.count);
      } else {
        // Show data for specific project
        const project = this.projects.find(p => p.key === this.selectedProject);
        if (project && project.issue_type_stats.length > 0) {
          this.chartData = [...project.issue_type_stats];
        } else {
          // Handle case when no data or very few tickets
          this.chartData = [];
        }
      }
      
      // Ensure chart works with any number of tickets (even 1, 2, or 3)
      if (this.chartData.length === 0) {
        this.chartData = [];
      } else if (this.chartData.length === 1) {
        // For single issue type, make it a full circle
        this.chartData[0].percentage = 100;
      } else {
        // For any number of tickets, ensure each gets a visible segment
        const totalTickets = this.chartData.reduce((sum, item) => sum + item.count, 0);
        
        if (totalTickets <= 5) {
          // For very small numbers (1-5 tickets), give each type equal percentage
          const equalPercentage = 100 / this.chartData.length;
          this.chartData.forEach(item => {
            item.percentage = Math.round(equalPercentage * 10) / 10;
          });
        } else {
          // For larger numbers, use actual percentages but ensure minimum visibility
          this.chartData.forEach(item => {
            if (item.percentage < 3 && item.count > 0) {
              item.percentage = 3; // Minimum 3% for visibility
            }
          });
          
          // Recalculate percentages to ensure they sum to 100%
          const totalPercentage = this.chartData.reduce((sum, item) => sum + item.percentage, 0);
          if (totalPercentage > 0) {
            this.chartData.forEach(item => {
              item.percentage = Math.round((item.percentage / totalPercentage) * 100 * 10) / 10;
            });
          }
        }
        
        // Final check: ensure no zero percentages
        this.chartData.forEach(item => {
          if (item.percentage === 0 && item.count > 0) {
            item.percentage = 1;
          }
        });
      }
    },
    updateAvailableIssueTypes() {
      const allTypes = new Set<string>();
      this.projects.forEach(project => {
        project.issue_type_stats.forEach(stat => {
          allTypes.add(stat.name);
        });
      });
      this.availableIssueTypes = Array.from(allTypes).sort();
    },
    getColorForIssueType(issueType: string): string {
      const colors: { [key: string]: string } = {
        'Bug': '#ce4110',
        'Task': '#f01792',
        'Sub-task': '#031f4b',
        'Technical Task': '#35ce7b',
        'Functional Requirement': '#7367f1',
        'Technical Requirement': '#e02020',
        'Deliverable': '#1ab1ff',
        'Production Support': '#f7b500',
        'Story': '#5a4a74',
        'Epic': '#7e3db2',
        'Improvement': '#4a6785',
        'New Feature': '#4a6785',
        'Wish': '#4a6785',
      };
      
      return colors[issueType] || '#666666';
    },
    getDonutPath(index: number, data: IssueTypeStat[]): string {
      const radius = 90; // Medium radius
      const centerX = 120; // Medium center X
      const centerY = 120; // Medium center Y
      const strokeWidth = 25; // Medium stroke width
      
      // Ensure we have valid data
      if (!data || data.length === 0) {
        return '';
      }
      
      let startAngle = 0;
      for (let i = 0; i < index; i++) {
        const percentage = data[i]?.percentage || 0;
        startAngle += (percentage / 100) * 2 * Math.PI;
      }
      
      const currentPercentage = data[index]?.percentage || 0;
      const endAngle = startAngle + (currentPercentage / 100) * 2 * Math.PI;
      
      const x1 = centerX + radius * Math.cos(startAngle);
      const y1 = centerY + radius * Math.sin(startAngle);
      const x2 = centerX + radius * Math.cos(endAngle);
      const y2 = centerY + radius * Math.sin(endAngle);
      
      const largeArcFlag = currentPercentage > 50 ? 1 : 0;
      
      // Create donut segment outline
      return `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`;
    },
    getTotalIssues(): number {
      return this.validChartData.reduce((total, item) => total + item.count, 0);
    },
    showTooltip(event: MouseEvent, item: IssueTypeStat, index: number) {
      this.tooltipData = item;
      this.tooltipVisible = true;
      this.hoveredSegmentIndex = index;
      
      const rect = (event.target as SVGElement).getBoundingClientRect();
      const chartContainer = (event.target as SVGElement).closest('.chart-container');
      const containerRect = chartContainer?.getBoundingClientRect();
      
      if (containerRect) {
        this.tooltipStyle = {
          left: `${event.clientX - containerRect.left}px`,
          top: `${event.clientY - containerRect.top}px`
        };
      }
    },
    hideTooltip() {
      this.tooltipVisible = false;
      this.tooltipData = null;
      this.hoveredSegmentIndex = -1;
    },
    cleanupSliders() {
      try {
        const $slider1 = $('.vertical-slider-1');
        const $slider2 = $('.vertical-slider-2');
        
        if ($slider1.hasClass('slick-initialized')) {
          $slider1.slick('unslick');
        }
        
        if ($slider2.hasClass('slick-initialized')) {
          $slider2.slick('unslick');
        }
      } catch (error) {
        console.warn('Error cleaning up sliders:', error);
      }
    },
    initializeAllProjectsSlider() {
      try {
        const $el = $('.vertical-slider-1');
        
        // More thorough cleanup
        if ($el.hasClass('slick-initialized')) {
          $el.slick('unslick');
        }
        
        // Wait for DOM to be ready
        this.$nextTick(() => {
          // Double check element still exists
          if ($el.length && $el.is(':visible')) {
            $el.slick({
              dots: false,
              vertical: true,
              centerMode: false,
              infinite: false,
              slidesToShow: 7,
              slidesToScroll: 2,
              adaptiveHeight: true,
              responsive: [{
                breakpoint: 991,
                settings: {
                  slidesToShow: 4,
                  slidesToScroll: 4
                }
              }, {
                breakpoint: 600,
                settings: {
                  slidesToShow: 2,
                  slidesToScroll: 2
                }
              }, {
                breakpoint: 480,
                settings: {
                  slidesToShow: 1,
                  slidesToScroll: 1
                }
              }]
            });
          }
        });
      } catch (error) {
        console.warn('Error initializing projects slider:', error);
      }
    },
    initializeIssueTypeSlider() {
      try {
        const $el = $('.vertical-slider-2');
        
        // More thorough cleanup
        if ($el.hasClass('slick-initialized')) {
          $el.slick('unslick');
        }
        
        // Wait for DOM to be ready
        this.$nextTick(() => {
          // Double check element still exists
          if ($el.length && $el.is(':visible')) {
            $el.slick({
              dots: false,
              vertical: true,
              centerMode: false,
              infinite: false,
              slidesToShow: 7,
              slidesToScroll: 2,
              adaptiveHeight: true,
              responsive: [{
                breakpoint: 991,
                settings: {
                  slidesToShow: 5,
                  slidesToScroll: 5
                }
              }, {
                breakpoint: 600,
                settings: {
                  slidesToShow: 2,
                  slidesToScroll: 2
                }
              }, {
                breakpoint: 480,
                settings: {
                  slidesToShow: 1,
                  slidesToScroll: 1
                }
              }]
            });
          }
        });
      } catch (error) {
        console.warn('Error initializing issue type slider:', error);
      }
    },
  },
  watch: {
    // Watch for changes in the route parameter when in project details mode
    '$route.params.projectKey': {
      handler(newKey, oldKey) {
        if (this.isProjectDetailsMode && newKey !== oldKey && newKey) {
          this.selectedProject = newKey as string;
          this.updateChartData();
          this.forceRenderKey++;
        }
      },
      immediate: false
    }
  }
});
</script>

<style scoped>
.vertical-slider-box {
  max-height: 400px;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  scrollbar-color: #ccc #f1f1f1;
}

.vertical-slider-box::-webkit-scrollbar {
  width: 6px;
}

.vertical-slider-box::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.vertical-slider-box::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 3px;
}

.vertical-slider-box::-webkit-scrollbar-thumb:hover {
  background: #999;
}

.vertical-slider-row {
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: center;
  min-height: 50px;
}

.vertical-slider-row:hover {
  background-color: #f8f9fa;
}

.vertical-slider-row.active {
  background-color: #e3f2fd;
  border-left: 3px solid #007bff;
}

.vertical-slider-row i {
  margin-right: 12px;
  display: flex;
  align-items: center;
}

.vertical-slider-row img {
  width: 24px;
  height: 24px;
  border-radius: 4px;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  width: 100%;
}


.chart-container {
  position: relative;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.no-data {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  color: #666;
  font-style: italic;
}




.donut-chart {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 20px 0 0;
  width: 100%;
  height: 100%;
  min-height: 250px;
}

.donut-chart-svg {
  width: 100%;
  height: auto;
  max-width: 300px;
  max-height: 300px;
}

.donut-segment {
  transition: all 0.3s ease;
  cursor: pointer;
}

.donut-segment:hover {
  opacity: 0.8;
  transform: scale(1.02);
}

.donut-center-text {
  font-size: 28px;
  font-weight: bold;
  fill: #333;
}

.donut-center-subtext {
  font-size: 14px;
  fill: #666;
}

.chart-tooltip {
  position: absolute;
  pointer-events: none;
  z-index: 1000;
  transform: translate(-50%, -100%);
  margin-top: -10px;
  min-width: 150px;
}

.tooltip-content {
  text-align: center;
  padding: 12px 16px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  position: relative;
}

.tooltip-name {
  font-size: 16px;
  font-weight: bold;
  color: #ffffff;
  margin-bottom: 8px;
}

.tooltip-count {
  font-size: 14px;
  color: #ffffff;
  margin-bottom: 4px;
}

.tooltip-percentage {
  font-size: 12px;
  color: #ffffff;
}

.tooltip-connector {
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 2px;
  height: 8px;
  border-radius: 1px;
}

.status-chart-details {
  transition: all 0.3s ease;
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
}

.status-chart-details:hover {
  background-color: #f8f9fa;
}

.custom-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.status-colors {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 10px;
  flex-shrink: 0;
}

.status-type {
  flex: 1;
  font-weight: 500;
}

.status-numbr {
  font-weight: bold;
  color: #333;
  min-width: 40px;
  text-align: right;
}

.status-done {
  font-weight: bold;
  color: #007bff;
  min-width: 50px;
  text-align: right;
}


</style>